import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Chip, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Cautelar, Cautelares, getCautelares } from "../services/oece.service";
import { C, Pill, Tarjeta, fecha, gridSx, num } from "./oeceUi";

const localeText = esES.components.MuiDataGrid.defaultProps.localeText;

// Color fijo por vía, el mismo en gráfico, filtros y tabla
const VIAS: Record<string, { color: string; bg: string; corto: string }> = {
  "Contencioso-administrativo": { color: C.navy2, bg: "#e9eef7", corto: "Contencioso" },
  "Constitucional (amparo)": { color: "#7a3fb0", bg: "#f2eafa", corto: "Amparo" },
  Civil: { color: "#0f8a8a", bg: "#e3f4f4", corto: "Civil" },
  Laboral: { color: C.amber, bg: C.amberSoft, corto: "Laboral" },
  "No indicada": { color: C.muted, bg: "#eef1f5", corto: "No indicada" },
};
const estiloVia = (v: string) => VIAS[v] ?? VIAS["No indicada"];

const PillVia = ({ via }: { via: string }) => {
  const e = estiloVia(via);
  return (
    <Box component="span" sx={{ display: "inline-flex", alignSelf: "center", height: 22, alignItems: "center", px: 1, borderRadius: 999, fontSize: 12, fontWeight: 700, bgcolor: e.bg, color: e.color, whiteSpace: "nowrap" }}>
      {e.corto}
    </Box>
  );
};

const Titulo = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.navy, mb: 1.5 }}>{children}</Typography>
);

interface Props {
  onVerFicha: (ruc: string) => void;
}

const CautelaresTab = ({ onVerFicha }: Props) => {
  const [data, setData] = useState<Cautelares | null>(null);
  const [error, setError] = useState("");
  const [via, setVia] = useState("");
  const [texto, setTexto] = useState("");
  const [copiado, setCopiado] = useState("");

  useEffect(() => {
    getCautelares()
      // id propio: la misma cautelar puede venir repetida (p. ej. empresas de un consorcio)
      .then((r) => setData({ ...r.data, items: r.data.items.map((i, k) => ({ ...i, id: k })) }))
      .catch(() => setError("No se pudieron cargar las medidas cautelares."));
  }, []);

  const filas = useMemo(() => {
    const q = texto.trim().toLowerCase();
    return (data?.items ?? []).filter(
      (i) =>
        (!via || i.via === via) &&
        (!q || [i.razon_social, i.ruc, i.organo, i.organo_clave, i.expediente_judicial, i.juez]
          .some((v) => v?.toLowerCase().includes(q)))
    );
  }, [data, via, texto]);

  const anioActual = new Date().getFullYear();
  const maxAnio = Math.max(1, ...(data?.por_anio.map((a) => a.n) ?? [1]));
  const aniosRecientes = (data?.por_anio ?? []).filter((a) => a.valor >= anioActual - 8);

  const copiar = (s: string) => {
    navigator.clipboard?.writeText(s);
    setCopiado(s);
    setTimeout(() => setCopiado(""), 1500);
  };

  const columnas: GridColDef<Cautelar>[] = [
    {
      field: "fecha_inicio", headerName: "Otorgada", width: 115,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fecha(row.fecha_inicio)}</Typography>
          {row.provisional && <Typography sx={{ fontSize: 11, color: C.amber, fontWeight: 600 }}>Provisional</Typography>}
        </Box>
      ),
    },
    {
      field: "razon_social", headerName: "Empresa", flex: 1.3, minWidth: 210,
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0, py: 1 }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: 13.5, color: C.navy }} title={row.razon_social ?? ""}>
            {row.razon_social || "Sin razón social"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.muted, fontFamily: "monospace" }}>{row.ruc}</Typography>
        </Box>
      ),
    },
    {
      field: "via", headerName: "Vía", width: 120,
      renderCell: ({ row }) => <PillVia via={row.via} />,
    },
    {
      field: "organo_clave", headerName: "Juzgado / Sala", flex: 1.5, minWidth: 230,
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>
            {row.organo_clave || row.organo || "No indicado"}
          </Typography>
          {row.juez && <Typography noWrap sx={{ fontSize: 12, color: C.muted }}>Juez: {row.juez}</Typography>}
        </Box>
      ),
    },
    {
      field: "expediente_judicial", headerName: "Expediente judicial", width: 290,
      renderCell: ({ row }) =>
        row.expediente_judicial ? (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography sx={{ fontSize: 12.5, fontFamily: "monospace", whiteSpace: "nowrap" }}>{row.expediente_judicial}</Typography>
            <Tooltip title={copiado === row.expediente_judicial ? "Copiado" : "Copiar"}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); copiar(row.expediente_judicial!); }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Typography variant="caption" color="text.disabled">No indicado</Typography>
        ),
    },
    {
      field: "resoluciones_sancion", headerName: "Sanción suspendida", width: 175, sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={row.descripcion} placement="left">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.35 }}>
              {row.resoluciones_sancion.slice(0, 2).join(" · ") || "Ver detalle"}
            </Typography>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: C.muted }} />
          </Stack>
        </Tooltip>
      ),
    },
  ];

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {/* ── Resumen ── */}
      <Box sx={{ display: "grid", gap: 2, mb: 2.5, gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr 1.3fr" } }}>
        <Tarjeta sx={{ p: 2.25 }}>
          <Titulo>Otorgadas por año</Titulo>
          <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ height: 130 }}>
            {aniosRecientes.map((a) => (
              <Tooltip key={a.valor} title={`${a.n} cautelares${a.valor === anioActual ? " (año en curso)" : ""}`}>
                <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, height: "100%", justifyContent: "flex-end" }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy }}>{a.n}</Typography>
                  <Box
                    sx={{
                      width: "100%", maxWidth: 34, borderRadius: "6px 6px 2px 2px",
                      height: `${(a.n / maxAnio) * 90}px`,
                      bgcolor: a.valor === anioActual ? "transparent" : C.navy2,
                      border: a.valor === anioActual ? `2px dashed ${C.navy2}` : "none",
                    }}
                  />
                  <Typography sx={{ fontSize: 11.5, color: C.muted }}>{a.valor}</Typography>
                </Stack>
              </Tooltip>
            ))}
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 1 }}>{anioActual}: año en curso (punteado).</Typography>
        </Tarjeta>

        <Tarjeta sx={{ p: 2.25 }}>
          <Titulo>Por vía judicial</Titulo>
          <Stack spacing={1.25}>
            {(data?.por_via ?? []).map((v) => {
              const e = estiloVia(v.valor);
              const activo = via === v.valor;
              return (
                <Box
                  key={v.valor}
                  onClick={() => setVia(activo ? "" : v.valor)}
                  sx={{ cursor: "pointer", opacity: via && !activo ? 0.45 : 1, transition: "opacity .15s" }}
                >
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                    <Typography sx={{ fontSize: 12.5, color: "#334155", fontWeight: activo ? 700 : 500 }}>{v.valor}</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: e.color }}>
                      {v.n} <span style={{ color: C.muted, fontWeight: 500 }}>· {Math.round((v.n / Math.max(1, data!.total)) * 100)}%</span>
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 7, bgcolor: "#edf0f5", borderRadius: 4, overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${(v.n / Math.max(1, data!.total)) * 100}%`, bgcolor: e.color, borderRadius: 4 }} />
                  </Box>
                </Box>
              );
            })}
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 1.25 }}>Clic en una vía para filtrar la lista.</Typography>
        </Tarjeta>

        <Tarjeta sx={{ p: 2.25 }}>
          <Titulo>Juzgados y salas que más las conceden</Titulo>
          <Stack spacing={0.75}>
            {(data?.por_organo ?? []).slice(0, 7).map((o, i) => (
              <Stack
                key={o.valor} direction="row" alignItems="center" spacing={1.25}
                onClick={() => setTexto(o.valor)}
                sx={{ cursor: "pointer", borderRadius: 1.5, px: 0.75, py: 0.4, "&:hover": { bgcolor: "#f3f6fb" } }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: i < 3 ? C.gold : C.muted, width: 16 }}>{i + 1}</Typography>
                <Typography sx={{ fontSize: 12.5, flex: 1, color: "#334155" }}>{o.valor}</Typography>
                <Pill tono="blue">{o.n}</Pill>
              </Stack>
            ))}
          </Stack>
        </Tarjeta>
      </Box>

      {/* ── Filtros ── */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar empresa, juzgado, juez o expediente…"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          sx={{ minWidth: { md: 380 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.muted }} /></InputAdornment>,
            sx: { bgcolor: "#fff", borderRadius: 2 },
          }}
        />
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {(data?.por_via ?? []).map((v) => {
            const e = estiloVia(v.valor);
            const activo = via === v.valor;
            return (
              <Chip
                key={v.valor}
                label={e.corto}
                onClick={() => setVia(activo ? "" : v.valor)}
                sx={{
                  fontWeight: 600, border: `1px solid ${activo ? e.color : C.border}`,
                  bgcolor: activo ? e.color : "#fff", color: activo ? "#fff" : e.color,
                  "&:hover": { bgcolor: activo ? e.color : e.bg },
                }}
              />
            );
          })}
        </Stack>
        <Typography sx={{ fontSize: 13, color: C.muted, ml: { md: "auto !important" }, whiteSpace: "nowrap" }}>
          <b style={{ color: C.navy }}>{num(filas.length)}</b> de {num(data?.total)} cautelares · {num(data?.empresas)} empresas
        </Typography>
      </Stack>

      <DataGrid
        rows={filas}
        columns={columnas}
        loading={!data}
        rowHeight={64}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={({ row }) => onVerFicha(row.ruc)}
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        pageSizeOptions={[25, 50, 100]}
        localeText={localeText}
        sx={gridSx}
      />

      <Typography sx={{ fontSize: 12, color: C.muted, mt: 1.5 }}>
        {data?.nota} Sirven para ver qué juzgados las conceden y citar precedentes; no dan la tasa de éxito real.
      </Typography>
    </Box>
  );
};

export default CautelaresTab;
